/* eslint-disable consistent-default-export-name/default-export-match-filename -- The wrapper preserves the upstream component name. */
import type { ExperienceSocialConnector } from '@logto/schemas';
import Original from 'monoai:original?containers/SocialSignInList/index';
import { useEffect, useState } from 'react';

import { selectStudioProvider } from '@/apis/experience/studio-choice';
import buttonStyles from '@/shared/components/Button/index.module.scss';

import styles from './index.module.scss';
import useSocial from './use-social';

type Props = {
  readonly className?: string;
  readonly socialConnectors?: ExperienceSocialConnector[];
};

const StudioOptions = ({ connector }: { readonly connector: ExperienceSocialConnector }) => {
  const { invokeSocialSignIn } = useSocial();
  const [expanded, setExpanded] = useState(false);
  const [busy, setBusy] = useState(false);
  async function signIn(provider: 'google' | 'microsoft') {
    if (busy) {
      return;
    }
    setBusy(true);
    try {
      selectStudioProvider(provider);
      await invokeSocialSignIn(connector);
    } finally {
      setBusy(false);
    }
  }
  const style = `${buttonStyles.button} ${buttonStyles.secondary} ${buttonStyles.large} ${styles.socialLinkButton}`;
  return (
    <div className={styles.socialLinkList}>
      <button
        type="button"
        className={style}
        aria-expanded={expanded}
        disabled={busy}
        onClick={() => {
          setExpanded(!expanded);
        }}
      >
        Sign in with Studio
      </button>
      {expanded && (
        <div className={styles.socialLinkList} role="group" aria-label="Studio sign-in providers">
          <button
            type="button"
            className={style}
            disabled={busy}
            onClick={() => {
              void signIn('google');
            }}
          >
            via Google
          </button>
          <button
            type="button"
            className={style}
            disabled={busy}
            onClick={() => {
              void signIn('microsoft');
            }}
          >
            via Microsoft
          </button>
        </div>
      )}
    </div>
  );
};

// eslint-disable-next-line react/function-component-definition -- Additive wrappers require function declarations.
export default function SocialSignInList(props: Props) {
  const [mode, setMode] = useState<'loading' | 'enabled' | 'legacy' | 'unavailable'>('loading');
  useEffect(() => {
    const abort = new AbortController();
    async function loadConfiguration() {
      try {
        const response = await fetch('/api/monoai/studio/configuration', { signal: abort.signal });
        const data: unknown = response.ok ? await response.json() : null;
        if (abort.signal.aborted) {
          return;
        }
        if (
          data &&
          typeof data === 'object' &&
          'enabled' in data &&
          typeof data.enabled === 'boolean'
        ) {
          setMode(data.enabled ? 'enabled' : 'legacy');
        } else {
          setMode('unavailable');
        }
      } catch {
        if (!abort.signal.aborted) {
          setMode('unavailable');
        }
      }
    }
    void loadConfiguration();
    return () => {
      abort.abort();
    };
  }, []);
  const studio = props.socialConnectors?.find(({ target }) => target === 'core');
  if (mode === 'legacy' || !studio) {
    return <Original {...props} />;
  }
  return (
    <>
      <Original
        {...props}
        socialConnectors={props.socialConnectors?.filter(({ target }) => target !== 'core')}
      />
      {mode === 'enabled' ? (
        <StudioOptions connector={studio} />
      ) : (
        <button
          disabled
          className={`${buttonStyles.button} ${buttonStyles.secondary} ${buttonStyles.large}`}
          type="button"
        >
          {mode === 'loading' ? 'Sign in with Studio' : 'Studio sign-in unavailable'}
        </button>
      )}
    </>
  );
}
/* eslint-enable consistent-default-export-name/default-export-match-filename */
